import {
  AdyenMandatedBy,
  GrowQuoteSessionView,
  PaymentsAdyenSessionDataView
} from "../../Contracts/AdyenSession";
import { create } from "zustand";
import { useRequestStore } from "../BackendStore";
import {
  AdyenPaymentMethodType,
  AdyenTransactionCreateRequest,
  AdyenTransactionCreateView,
  BacsResponse,
  GiactValidationRequest,
  GiactValidationView,
  QuotePaymentResult
} from "../../Contracts/Transaction";
import { useBannerStore } from "../Banner";
import { BannerType } from "../../Contracts/Banner";

const getGrowQuoteSessionDetailsUrl = "/v1/payments/grow/quote/session/{sessionId}";
const createAdyenSessionUrl = "/v1/payments/grow/quote/session/{sessionId}/create";
const cancelPaymentUrl = "/v1/payments/grow/quote/session/{sessionId}/cancelPayment/";
const giactValidationUrl = "/v1/payments/grow/quote/transaction/giactValidation";
const createTransactionForQuoteUrl = "/v1/payments/grow/quote/transaction/create";
const getTransactionCreateResultUrl =
  "/v1/payments/grow/quote/transaction-creation-result/<transactionRef>/";
const handleBacsPaymentUrl = "/v1/payments/grow/quote/transaction/handleBacsPayment/";

interface ZomentumGrowQuoteState {
  isLoading: boolean;
  growQuoteSessionView?: GrowQuoteSessionView;
  adyenTransactionCreateView?: AdyenTransactionCreateView;
  getGrowQuoteSessionDetails: (sessionId: string) => Promise<GrowQuoteSessionView>;
  adyenCheckoutSessionView?: PaymentsAdyenSessionDataView;
  createAdyenSession: (sessionId: string, method: AdyenPaymentMethodType) => Promise<void>;
  cancelPayment: (sessionId: string) => Promise<void>;
  doGiactValidation: (
    giactValidationRquest: GiactValidationRequest
  ) => Promise<GiactValidationView>;
  createTransactionForQuote: (
    adyenTransactionCreateRequest: AdyenTransactionCreateRequest
  ) => Promise<AdyenTransactionCreateView>;
  getTransactionCreateResult: (
    transactionRef: string,
    resultCode: string
  ) => Promise<QuotePaymentResult>;
  handleBacsPayment: (
    transactionRef: string,
    bacsPaymentData: any,
    sessionUuid: string
  ) => Promise<BacsResponse>;
}

export const useZomentumGrowQuoteStore = create<ZomentumGrowQuoteState>(set => {
  const { getRequestNoAuth, postRequestNoAuth } = useRequestStore.getState();
  const { addBanner } = useBannerStore.getState();
  const getGrowQuoteSessionDetails = async (sessionId: string) => {
    set({ isLoading: true });
    const response = await getRequestNoAuth<GrowQuoteSessionView>(
      getGrowQuoteSessionDetailsUrl.replace("{sessionId}", sessionId)
    );
    set({ growQuoteSessionView: response, isLoading: false });
    return response;
  };

  const createAdyenSession = async (sessionId: string, method: AdyenPaymentMethodType) => {
    try {
      set({ isLoading: true });
      const response = await postRequestNoAuth<
        { method: AdyenPaymentMethodType },
        PaymentsAdyenSessionDataView
      >(createAdyenSessionUrl.replace("{sessionId}", sessionId), {
        method
      });
      set({ adyenCheckoutSessionView: response, isLoading: false });
    } catch (error) {
      console.error("Error creating Adyen session", error);
    }
  };

  const cancelPayment = async (sessionId: string) => {
    try {
      set({ isLoading: true });
      await getRequestNoAuth(cancelPaymentUrl.replace("{sessionId}", sessionId));
      set({ isLoading: false });
      addBanner({
        type: BannerType.Success,
        message: "Payment cancelled successfully"
      });
    } catch (error) {
      console.error("Error cancelling payment", error);
    }
  };

  const doGiactValidation = async (giactValidationRquest: GiactValidationRequest) => {
    set({ isLoading: true });
    const response = await postRequestNoAuth<GiactValidationRequest, GiactValidationView>(
      giactValidationUrl,
      giactValidationRquest
    );
    set({ isLoading: false });
    return response;
  };

  const createTransactionForQuote = async (
    adyenTransactionCreateRequest: AdyenTransactionCreateRequest
  ) => {
    set({ isLoading: true });
    const response = await postRequestNoAuth<
      AdyenTransactionCreateRequest,
      AdyenTransactionCreateView
    >(createTransactionForQuoteUrl, adyenTransactionCreateRequest);
    set({ adyenTransactionCreateView: response, isLoading: false });
    return response;
  };

  const getTransactionCreateResult = async (transactionRef: string, resultCode: string) => {
    set({ isLoading: true });
    const response = await getRequestNoAuth<QuotePaymentResult>(
      getTransactionCreateResultUrl
        .replace("<transactionRef>", transactionRef)
        .replace("<resultCode>", resultCode)
    );
    set({ isLoading: false });
    return response;
  };

  const handleBacsPayment = async (
    transactionRef: string,
    bacsPaymentData: any,
    sessionUuid: string
  ) => {
    set({ isLoading: true });
    const response = await postRequestNoAuth<
      {
        transaction_ref: string;
        mandated_by: AdyenMandatedBy;
        payment_method_data: any;
        session_uuid: string;
      },
      BacsResponse
    >(handleBacsPaymentUrl.replace("<transactionRef>", transactionRef), {
      transaction_ref: transactionRef,
      mandated_by: AdyenMandatedBy.Self,
      payment_method_data: bacsPaymentData,
      session_uuid: sessionUuid
    });
    set({ isLoading: false });
    return response;
  };

  return {
    isLoading: true,
    getGrowQuoteSessionDetails,
    createAdyenSession,
    cancelPayment,
    doGiactValidation,
    createTransactionForQuote,
    getTransactionCreateResult,
    handleBacsPayment
  };
});
